import { api, uploadForm } from '../lib/api.js';
import type { ProcessLabelRule } from '../lib/processLabel.js';

export type LaserNestDto = {
  fileName: string;
  materialRaw: string | null;
  material: string | null;
  thicknessMm: number | null;
  sheetWeightKg: number | null;
  partWeightKg: number | null;
  efficiencyPct: number | null;
  sheetSize: string | null;
  sheetQty: number | null;
};

export type LaserLine =
  | {
      status: 'ok';
      rawName: string;
      partNumber: string;
      /** השם („משולש"), כשיש. אינו המק״ט — עד 16/09/2026 השדה הזה החזיק מק״ט. */
      partName: string | null;
      quantity: number;
      unitsReported: number;
      cutSeconds: number;
      minutes: number;
      clamped: boolean;
      /**
       * משקל יחידה אחת, מדוח המכונה. `null` = לא נמסר, ו-0 = נכנס לעיגול של
       * שתי ספרות. בשני המקרים אין הצעת הדפסה לפי כמות — ולא „קל מחצי קילו".
       */
      unitWeightKg: number | null;
      /** הגרסה הפעילה. נוסעת בברקוד של המדבקה. */
      partVersion: number;
      /**
       * „3/12" — מיקום בניסט. אלי, 22/09/2026: „מדבקה צריך לכלול את מספר החלק
       * לפי החיתוך." ⚠ עד 24/09 נשלח רק בהדפסה החוזרת מהלוח, ולכן אף מדבקה
       * שהודפסה **בעמדה**, מיד אחרי ההעלאה, לא נשאה אותו. נמדד בסימולציה.
       */
      nestIndex: number | null;
      nestPartCount: number;
    }
  | {
      status: 'skipped';
      rawName: string;
      partNumber: string | null;
      reason: string;
      /**
       * החלק נחתך פיזית ויש לו פרויקט — כלומר מגיעה לו מדבקה, גם בלי שרטוט.
       *
       * אלי, 23/09/2026: „פרויקט שנחתך בלייזר ואין לו פרויקט במערכת, תדלג
       * עליו בעקיבות ומדבקה. יש לו פרויקט במערכת — תוציא לו מדבקה. גם אם
       * אין לו שרטוט."
       *
       * `false` = אין פרויקט לשייך אליו, או שהחיתוך חסום (פרויקט מבוטל,
       * ECO פתוח, אי-התאמה) — שם מדבקה הייתה מכשירה חיתוך שלא היה אמור
       * לקרות.
       */
      labelable: boolean;
      /** מה שהקובץ ביקש. אין `unitsReported` — דבר לא דווח. */
      quantity: number;
      unitWeightKg: number | null;
      /**
       * „3/12" — מיקום בניסט. אלי, 22/09/2026: „מדבקה צריך לכלול את מספר החלק
       * לפי החיתוך." ⚠ עד 24/09 נשלח רק בהדפסה החוזרת מהלוח, ולכן אף מדבקה
       * שהודפסה **בעמדה**, מיד אחרי ההעלאה, לא נשאה אותו. נמדד בסימולציה.
       */
      nestIndex: number | null;
      nestPartCount: number;
    };

// ── What the station SEES ──────────────────────────────────────────
//
// Badge-only, like the rest of the kiosk surface, and deliberately carrying no
// ₪: the cost of a sheet is read on /app/laser-materials behind a capability.

export type LaserQueueRow = {
  partId: string;
  partNumber: string;
  partName: string | null;
  projectNumber: string;
  projectName: string | null;
  customerName: string | null;
  quantity: number;
  remaining: number;
  sequenceIndex: number;
  targetDate: string | null;
  daysToDelivery: number | null;
  waitingDays: number;
  material: string | null;
  materialLabel: string | null;
  thicknessMm: number | null;
  thumbnailUrl: string | null;
  /**
   * „עלתה רוויזיה — בדוק נסטים מתוכנתים" (אלי, 24/09/2026). רוויזיה שעלתה
   * בשבוע האחרון אחרי החיתוך האחרון של החלק. `null` = אין על מה להתריע.
   */
  revisedTo: { revision: string | null; at: string } | null;
};

export type LaserQueueGroup = {
  key: string;
  material: string | null;
  /** `null` = החומר עוד לא ידוע. המסך אומר זאת בשפה של הקורא. */
  materialLabel: string | null;
  thicknessMm: number | null;
  parts: number;
  units: number;
  projects: number;
};

export type LaserQueue = {
  rows: LaserQueueRow[];
  groups: LaserQueueGroup[];
  totals: { parts: number; units: number; projects: number; unknownMaterial: number };
};

export type LaserHistoryRow = {
  id: string;
  fileName: string;
  uploadedAt: string;
  operatorName: string;
  material: string;
  thicknessMm: number | null;
  sheetWeightKg: number | null;
  partWeightKg: number | null;
  efficiencyPct: number | null;
  sheetSize: string | null;
  sheetQty: number | null;
  partsReported: number;
  partsSkipped: number;
  drawingsMissing: number;
  projects: string[];
};

/** שורה ברשימת הבחירה של „קשר לחלק קיים". */
export type LinkCandidate = {
  id: string;
  partNumber: string;
  name: string | null;
  quantity: number;
  routeName: string;
  createdAt: string;
  hasDrawing: boolean;
  /** ⚠ דירוג ולא סינון: המק״ט זהה עד כדי אפסים מובילים. */
  similar: boolean;
  /**
   * הרוויזיה שתיכתב אם **זה** ייבחר.
   *
   * אלי, 23/09/2026: „כל דבר שאבחר שכבר היה קיים תוסיף רוויזיה אוטומטית.
   * כל דבר שאבחר ונחתך יישאר באותה רוויזיה."
   */
  nextRevision: string;
};

export type LinkCandidates = { partNumber: string; candidates: LinkCandidate[] };

export type LaserDrawingRequestRow = {
  id: string;
  projectNumber: string;
  partNumber: string;
  rawName: string;
  materialCode: string | null;
  materialLabel: string | null;
  thicknessMm: number | null;
  quantity: number | null;
  reason: string;
  raisedAt: string;
  projectId: string | null;
  /** האם יש `Part` במערכת — כשאין, אין ממה לרשת מסלול וכמות. */
  partExists: boolean;
  /** מסלול החלק הקיים, כדי שהעלאה מהרשימה לא תבקש לבחור מחדש. */
  routeName: string | null;
  /** הכמות הרשומה על החלק (להבדיל מהכמות שנחתכה בניסט). */
  partQuantity: number | null;
  /** המהדורה הבאה אחרי זו שהחלק נמצא בה — ראה `nextRevision` בשרת. */
  nextRevision: string;
  /** החלק הקיים (כשיש) — עליו מכריעים „אשר עבודה בלי שרטוט". */
  partId?: string | null;
  /** ההכרעה על חלק קיים בלי שרטוט פעיל. `null` = אין חלק, או שיש לו שרטוט. */
  hold?: { decision: HoldDecision | null; effective: HoldDecision; byName: string | null } | null;
};

export type HoldDecision = 'WAIT' | 'WAIVED';

/** סיבות הביטול — אלי, 24/09/2026. */
export type LaserCancelReason = 'RENAMED' | 'PROJECT_UNTRACKED' | 'CUT_ERROR' | 'OTHER';

/** ביטול אחד — מקור הנתונים של בונה הדוחות ושל „בוטלו לאחרונה". */
export type LaserCancelRow = {
  id: string; reason: LaserCancelReason; note: string | null;
  projectNumber: string; partNumber: string; linkedPartNumber: string | null;
  quantity: number | null; materialCode: string | null; thicknessMm: number | null;
  machineMinutes: number | null; kg: number | null;
  /** `null` כשאין הרשאת צפייה בעלויות — לא „אפס". */
  costIls: number | null;
  cancelledByName: string; cancelledAt: string;
  restoredAt: string | null; restoredByName: string | null;
  groupSize: number;
};

/**
 * שורה אחת מתוך ניסט שנחתך — מה שהיה על הפח, ואיפה השרטוט.
 *
 * `drawing` נקרא בשרת **עכשיו** ולא מהצילום שנשמר בזמן החיתוך:
 * `ok` = יש גרסה פעילה (PDF שהועלה), `requested` = אין, ויש בקשת שרטוט
 * פתוחה, `missing` = אין גרסה פעילה ואיש לא נשאל.
 */
export type LaserNestLine = {
  rawName: string;
  partNumber: string | null;
  partName: string | null;
  projectNumber: string | null;
  quantity: number;
  unitWeightKg: number | null;
  sizeXmm: number | null;
  sizeYmm: number | null;
  cutSeconds: number;
  drawing: 'ok' | 'requested' | 'missing';
  drawingVersion: number | null;
  requestReason: string | null;
  requestedAt: string | null;
  resolvedAt: string | null;
  /** השרטוט הגיע — אחרי שהפח כבר נחתך. */
  arrivedAfterCut: boolean;
};

export type LaserTotals = { reportedParts: number; skippedParts: number; totalUnits: number; totalMinutes: number };

/**
 * שורה שאפשר להדפיס לה מדבקות — מתוך העלאה שנבחרה, או מחיפוש מק״ט.
 *
 * `unitWeightKg` נשמר על השורה בזמן החיתוך ולא נשלף מהחלק, ולכן הדפסה
 * חוזרת אחרי חודשים עדיין שוקלת את מה שנחתך אז. `null` (או 0, שהוא עיגול
 * של המכונה לשתי ספרות) = אין הצעה, ולא „קל מחצי קילו".
 */
export type LaserPrintLine = {
  uploadId: string;
  fileName: string;
  uploadedAt: string;
  partNumber: string;
  partName: string | null;
  quantity: number;
  unitWeightKg: number | null;
  material: string | null;
  thicknessMm: number | null;
  /** הגרסה הפעילה — נוסעת בברקוד. `null` = אין שרטוט פעיל, ואז אין ברקוד. */
  partVersion: number | null;
  /**
   * „No." — מספר החלק **בתוך החיתוך**, ומתוך כמה. מודפס „3/12".
   *
   * אלי, 22/09/2026: „מדבקה צריך לכלול את מספר החלק לפי החיתוך." ⚠ באותו
   * קובץ שני חלקים נושאים אותו מק״ט בשני גדלים שונים — בלי המספר הזה שתי
   * המדבקות זהות. `null` = העלאה שקדמה לשדה, ואז לא מודפס דבר.
   */
  nestIndex: number | null;
  nestPartCount: number | null;
  /** אין שרטוט פעיל — המדבקה אומרת „⚠ ממתין לשרטוט", גם בהדפסה חוזרת. */
  awaitingDrawing: boolean;
};

export type LaserPreviewDto = {
  operator: { employeeNumber: string; name: string };
  nest: LaserNestDto;
  setupMinutes: number;
  lines: LaserLine[];
  totals: LaserTotals;
};

export type LaserCommitDto = LaserPreviewDto & { laserUploadId: string; committedAt: string };

async function laserUpload(employeeNumber: string, file: File, dryRun: boolean): Promise<LaserPreviewDto | LaserCommitDto> {
  const fd = new FormData();
  fd.append('employeeNumber', employeeNumber);
  fd.append('file', file, file.name);
  const url = dryRun ? '/api/laser/upload?dryRun=true' : '/api/laser/upload';
  // ⚠ קובץ ה-nesting של הלייזר הוא מהגדולים שעולים לכאן, ולכן 413 מ-nginx
  // דווקא כאן סביר — `uploadForm` אומר שהקובץ גדול מדי ומה לעשות, במקום
  // „שגיאת רשת" שהיה נאמר קודם על גוף HTML שלא נפרס.
  return uploadForm<LaserPreviewDto | LaserCommitDto>(url, fd);
}

export type LaserMaterialRow = {
  material: string;
  thicknessMm: number | null;
  sheetWeightKg: number;
  partWeightKg: number;
  efficiencyPct: number | null;
  sheets: number;
  uploads: number;
  materialCostIls: number | null;
  labourCostIls: number | null;
  unpricedUploads: number;
};

export type SheetPriceRow = {
  id: string; material: string;
  /** 0 = any thickness. */
  thicknessMm: number;
  pricePerKg: number; note: string | null;
  /** Parsed from the material name on save — this is what actually matches. */
  grade: string | null;
  updatedAt: string; updatedByName: string | null; kgLastYear: number;
};

export type UnpricedMaterial = { material: string; thicknessMm: number | null; kg: number; uploads: number };

/** A candidate price read out of the purchasing catalog — proposed, not applied. */
export type SheetPriceSuggestion = {
  sku: string; name: string; pricePerKg: number;
  grade: string | null; thicknessMm: number | null; stockQty: number;
  /** Too expensive to be ₪ per kilo — almost certainly a price per sheet. */
  likelyPerSheet: boolean;
  alreadyPriced: boolean;
};

function rangeQuery(from?: string, to?: string): string {
  const p = new URLSearchParams();
  if (from) p.set('from', from);
  if (to) p.set('to', to);
  const s = p.toString();
  return s ? `?${s}` : '';
}


/** Sheet metal a project pulled off the laser — see laser-material.ts. */
export type LaserProjectMaterial = {
  material: string;
  thicknessMm: number | null;
  kg: number;
  /** null when the matrix does not allow this person to see costs. */
  cost: number | null;
  sheets: number;
  /** At least one of those sheets had no price — the ₪ figure is short. */
  partlyUnpriced: boolean;
  /** weight | area | minutes | equal — איך הפח חולק בין החלקים. */
  basis: string;
};

export const laserApi = {
  projectMaterial: (number: string) =>
    api<LaserProjectMaterial[]>(`/api/laser/project/${encodeURIComponent(number)}/material`),
  preview: (employeeNumber: string, file: File) =>
    laserUpload(employeeNumber, file, true) as Promise<LaserPreviewDto>,
  commit: (employeeNumber: string, file: File) =>
    laserUpload(employeeNumber, file, false) as Promise<LaserCommitDto>,
  // GET רגיל שנכתב ביד — `api()` עושה בדיוק את זה, ובנוסף תופס נפילת רשת
  // ותשובת HTML של 200 (טאב שנשאר פתוח מעל פריסה).
  materials: (from?: string, to?: string) =>
    api<LaserMaterialRow[]>(`/api/laser/materials${rangeQuery(from, to)}`),
  materialsExportUrl: (from?: string, to?: string): string =>
    `/api/laser/materials/export${rangeQuery(from, to)}`,
  prices: () => api<SheetPriceRow[]>('/api/laser/prices'),
  priceSuggestions: () => api<SheetPriceSuggestion[]>('/api/laser/prices/suggest'),
  unpricedMaterials: () => api<UnpricedMaterial[]>('/api/laser/prices/missing'),
  setPrice: (body: { material: string; thicknessMm: number; pricePerKg: number; note?: string | null }) =>
    api<{ id: string }>('/api/laser/prices', { method: 'POST', body }),
  removePrice: (id: string) =>
    api<{ ok: true }>(`/api/laser/prices/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Kiosk-public, like the upload itself.
  printLines: (filter: { uploadId?: string; partNumber?: string }) => {
    const qs = new URLSearchParams();
    if (filter.uploadId) qs.set('uploadId', filter.uploadId);
    if (filter.partNumber) qs.set('partNumber', filter.partNumber);
    return api<LaserPrintLine[]>(`/api/laser/print-lines?${qs.toString()}`);
  },
  /** מה היה על ניסט אחד. קיוסק-ציבורי כמו שאר הלוח, ובלי ₪. */
  nestLines: (uploadId: string) =>
    api<LaserNestLine[]>(`/api/laser/nest/${encodeURIComponent(uploadId)}/lines`),
  queue: () => api<LaserQueue>('/api/laser/queue'),
  history: (limit?: number) =>
    api<LaserHistoryRow[]>(`/api/laser/history${limit ? `?limit=${limit}` : ''}`),
  drawingRequests: () => api<LaserDrawingRequestRow[]>('/api/laser/drawing-requests'),

  /**
   * החלקים שאפשר לקשר אליהם בקשת שרטוט — דומים בשם למעלה, ואחריהם כל
   * חלקי הפרויקט לפי סדר כרונולוגי (אלי, 23/09/2026).
   */
  linkCandidates: (requestId: string) =>
    api<LinkCandidates>(`/api/laser/missing-drawings/${encodeURIComponent(requestId)}/candidates`),

  /** „החיתוך עובר לחלק הנכון" — הכמות והדקות נרשמות על החלק שנבחר. */
  linkToPart: (requestId: string, partId: string) =>
    api<{ lines: number; units: number; minutes: number; partNumber: string }>(
      `/api/laser/missing-drawings/${encodeURIComponent(requestId)}/link`,
      // ⚠ האובייקט עצמו: `api()` כבר עושה `JSON.stringify`. מחרוזת כאן
      // נשלחת כ-JSON של מחרוזת, והשרת מקבל טקסט במקום גוף.
      { method: 'POST', body: { partId } },
    ),
  /** „בטל" עם סיבה חובה — כל מהנדס ומעלה (אלי, 24/09/2026). */
  cancelRequest: (requestId: string, body: { reason: LaserCancelReason; note?: string | null; partId?: string | null }) =>
    api<{ reason: LaserCancelReason; closed: number; linkedPartNumber: string | null; units: number }>(
      `/api/laser/missing-drawings/${encodeURIComponent(requestId)}/cancel`,
      { method: 'POST', body },
    ),
  restoreCancel: (cancelId: string) =>
    api<{ restored: number }>(`/api/laser/cancels/${encodeURIComponent(cancelId)}/restore`, { method: 'POST', body: {} }),
  cancels: () => api<LaserCancelRow[]>('/api/laser/cancels'),

  /**
   * כלל המדבקה שהמשרד ערך ב„הגדרות ← מדבקות".
   *
   * ⚠ הנתיב פתוח לקיוסק בכוונה (ראה `labels.routes`): המסור, המחסן
   * והלייזר שואלים „אילו שדות מודפסים" בלי סשן משרדי.
   */
  labelRule: (process: string) =>
    api<ProcessLabelRule>(`/api/labels/rules/${encodeURIComponent(process)}`),
};
